import type { ColDef, ColGroupDef } from 'ag-grid-community';
import type { PnlAxisColumn, PnlAxisHeader } from 'src/components/star-grid/pnlAxis.types';
import type { PnlGridRow } from 'src/components/star-grid/starGridTypes';
import { buildNumericColDef } from 'src/components/star-grid/utils/pnlGrid';
import { pnlAxisField } from 'src/components/star-grid/utils/pnlAxisColumns';

type DisplayHeader = PnlAxisHeader & { readonly tooltip?: string };
type GridColumn = ColDef<PnlGridRow> | ColGroupDef<PnlGridRow>;
const appendLeaf = (target: GridColumn[], path: readonly DisplayHeader[], leaf: ColDef<PnlGridRow>, occurrence: string): void => {
  const header = path[0];
  if (!header) {
    target.push(leaf);
    return;
  }
  const last = target[target.length - 1];
  const key = `${occurrence}/${encodeURIComponent(header.id)}`;
  const group = last && 'children' in last && last.headerName === header.label && last.groupId?.startsWith(`${key}:`) ? last : undefined;
  const next = group ?? {
    headerName: header.label,
    headerTooltip: header.tooltip ?? header.label,
    groupId: `${key}:${target.length}`,
    marryChildren: true,
    headerClass: 'sg-report-scenario-header',
    children: []
  };
  if (!group) target.push(next);
  appendLeaf(next.children, path.slice(1), leaf, next.groupId!);
};
export const buildReportAxisColumns = (
  columns: readonly PnlAxisColumn[],
  leafStyle: (column: ColDef<PnlGridRow>) => ColDef<PnlGridRow>,
  spacer: (index: number) => ColDef<PnlGridRow>,
  compactHeaders = false
): GridColumn[] => {
  const result: GridColumn[] = [];
  for (const column of columns) {
    if (column.kind === 'spacer') {
      result.push(spacer(column.index));
      continue;
    }
    const headers = column.header_path ?? (column.group ? [{ id: column.group, label: column.group }] : []);
    const path =
      compactHeaders && headers.length > 1
        ? [
            {
              id: JSON.stringify(headers.map((header) => header.id)),
              label: column.group ?? headers[0].label,
              tooltip: headers.map((header) => header.label).join(' · ')
            }
          ]
        : headers;
    const leaf = leafStyle({
      ...buildNumericColDef(pnlAxisField(column.index), column.label),
      colId: column.id ?? pnlAxisField(column.index),
      headerComponentParams: {
        coverage: column.coverage,
        periodType: column.periodType ?? (column.kind === 'variance' ? (column.exfx ? 'varianceExFx' : 'variance') : undefined)
      },
      // `derived` lets the formatter state a ratio row's variance in bps and its own cells as a %.
      context: { format: column.format, derived: column.kind === 'variance' }
    });
    const presentation =
      column.kind === 'attribute'
        ? {
            ...leaf,
            type: undefined,
            valueGetter: undefined,
            valueFormatter: undefined,
            cellClass: undefined,
            cellClassRules: undefined,
            filter: 'agTextColumnFilter',
            width: 150,
            minWidth: 120
          }
        : leaf;
    appendLeaf(result, path, { ...presentation, aggFunc: undefined, enableValue: false, allowedAggFuncs: [], sortable: false }, 'axis');
  }
  return result;
};
